import { useMemo } from 'react';
import {
  clampUnit,
  type FloorplanDimensions,
  type PlanPoint,
} from '@/features/inventorization/position/floorplan-marker-geometry';
import {
  type GeoPoint,
  GoogleMarkerMapPicker,
} from '@/routers/inventorization/components/position/GoogleMarkerMapPicker';
import { PlainFloorplanPicker } from '@/routers/inventorization/components/position/PlainFloorplanPicker';
import { config } from '@/shared/config/index';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

const GOOGLE_FLOORPLAN_CENTER = { lat: 0.5, lng: 0.5 };
const GOOGLE_FLOORPLAN_BLANK_STYLES = [{ featureType: 'all', stylers: [{ visibility: 'off' }] }];

export function FloorplanMarkerPicker({
  existingPins,
  floorplanDimensions,
  imageUrl,
  onPick,
  selectedMarker,
}: {
  existingPins: PlanPoint[];
  floorplanDimensions: FloorplanDimensions | null;
  imageUrl: string;
  onPick: (point: PlanPoint) => void;
  selectedMarker: PlanPoint | null;
}) {
  const floorplanRatio = readFloorplanRatio(floorplanDimensions);
  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      center: { ...GOOGLE_FLOORPLAN_CENTER, lng: floorplanRatio / 2 },
      clickableIcons: false,
      disableDoubleClickZoom: true,
      fullscreenControl: false,
      gestureHandling: 'greedy',
      mapTypeControl: false,
      streetViewControl: false,
      styles: GOOGLE_FLOORPLAN_BLANK_STYLES,
      tilt: 0,
      zoom: 18,
      zoomControl: true,
    }),
    [floorplanRatio],
  );
  const overlay = useMemo(
    () => ({
      bounds: () => createGoogleFloorplanBounds(floorplanRatio),
      imageUrl,
    }),
    [floorplanRatio, imageUrl],
  );
  const referenceMarkers = useMemo(
    () =>
      existingPins.map(pin => ({
        color: '#595959',
        point: createGoogleFloorplanLatLng(pin, floorplanRatio),
        scale: 6,
        shape: 'pin' as const,
        title: pin.key
          ? translateInventoryText('app.inventory.position.map.meterTitle', 'Meter {key}', { key: pin.key })
          : translateInventoryText('app.inventory.position.floorplan.floorMeter', 'Floor meter'),
        zIndex: 10,
      })),
    [existingPins, floorplanRatio],
  );
  const selectedGoogleMarker = useMemo(
    () =>
      selectedMarker
        ? {
            color: '#f5222d',
            label: 'M',
            point: createGoogleFloorplanLatLng(selectedMarker, floorplanRatio),
            title: translateInventoryText('app.inventory.position.map.thisMeter', 'This meter'),
          }
        : null,
    [floorplanRatio, selectedMarker],
  );

  return (
    <GoogleMarkerMapPicker
      apiKey={config.googleMapsApiKey}
      failedFallback={
        <PlainFloorplanPicker
          existingPins={existingPins}
          imageUrl={imageUrl}
          onPick={onPick}
          selectedMarker={selectedMarker}
        />
      }
      height={420}
      loadingDescription={translateInventoryText(
        'app.inventory.position.floorplan.loadingZoomable',
        'Loading zoomable floorplan...',
      )}
      mapOptions={mapOptions}
      onPick={point => onPick(readGoogleFloorplanPoint(point, floorplanRatio))}
      overlay={overlay}
      referenceMarkers={referenceMarkers}
      selectedMarker={selectedGoogleMarker}
    />
  );
}

function createGoogleFloorplanBounds(floorplanRatio: number) {
  const bounds = new google.maps.LatLngBounds();

  bounds.extend({ lat: 0, lng: 0 });
  bounds.extend({ lat: 1, lng: floorplanRatio });

  return bounds;
}

function createGoogleFloorplanLatLng(point: PlanPoint, floorplanRatio: number): GeoPoint {
  return {
    lat: 1 - clampUnit(point.y),
    lng: clampUnit(point.x) * floorplanRatio,
  };
}

function readGoogleFloorplanPoint(point: GeoPoint, floorplanRatio: number): PlanPoint {
  return {
    x: clampUnit(point.lng / floorplanRatio),
    y: clampUnit(1 - point.lat),
  };
}

function readFloorplanRatio(dimensions: FloorplanDimensions | null) {
  return dimensions?.width && dimensions.height ? dimensions.width / dimensions.height : 1;
}
